import styles from "./ParameterEnum.module.css";
import cn from "classnames";
import {
  ClickPopover,
  ClickPopoverProps,
} from "src/components/ui-components/ClickPopover";
import { ParamMenu } from "./ParamMenu";
import { Badge } from "src/components/ui-components/Badge/Badge";

import { useParameterEnumOptions } from "src/hooks/useParameterEnumOptions";

import type { ReactNode } from "react";
import type { Parameter } from "src/utils/parameters/types";
import type { FormFieldProps } from "src/components/ui-components/Input/FormField";

interface Props {
  parameter: Parameter;
  parameterValues: Exclude<Parameter["values"], undefined>;
  onChange?: (parameter: Parameter) => void;
  disabled?: boolean;

  renderFormField: (props: {
    renderButton: FormFieldProps["renderInput"];
    disabled: boolean;
  }) => ReactNode;

  renderInPortal?: ClickPopoverProps["renderInPortal"];
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

export function ParameterEnum(props: Props) {
  const {
    parameter,
    parameterValues: values,
    onChange,
    renderInPortal,
  } = props;

  const enumOptions = useParameterEnumOptions(parameter) ?? [];

  const disabled =
    (props.disabled ?? false) || enumOptions.length === 0 || !onChange;

  return (
    <ClickPopover
      position="below-align-left"
      content={() =>
        disabled ? null : (
          <div onKeyDown={props.onKeyDown}>
            <ParamMenu
              parameter={parameter}
              parameterValues={values}
              onChange={onChange}
              renderFormField={props.renderFormField}
            />
          </div>
        )
      }
      disabled={disabled}
      renderInPortal={renderInPortal}
    >
      <div
        className={cn(styles.fieldWrapper, disabled && styles.disabled)}
        onKeyDown={props.onKeyDown}
      >
        {props.renderFormField({
          disabled,
          renderButton: (props) => (
            <button type="button" {...props} className={styles.button}>
              <span className={styles.badges}>
                {values.length === 0
                  ? "None"
                  : values.slice(0, MAX_BADGES).map((value) => (
                      <Badge
                        key={value}
                        size="L"
                        color="brand-orange"
                        variant="filled"
                        className={styles.badge}
                      >
                        {value}
                      </Badge>
                    ))}
              </span>
              {values.length > MAX_BADGES && <>…</>}
            </button>
          ),
        })}
      </div>
    </ClickPopover>
  );
}

const MAX_BADGES = 4;
